import React from 'react'
import styled from 'styled-components'
import { useRevealProps } from '../../MwPage/components/Reveal'
import { IDS } from '../../MwPage/content'
import {
  cyan,
  dark,
  Eyebrow,
  Hi,
  ink,
  Lede,
  onDark,
  sans,
  Section,
  SectionHead,
  Small,
  Stack,
} from '../../MwPage/styled'
import { useFocus } from '../focus'
import { Bullets, Card, Column, Grid, H2, Kicker, Step, StepNum, Steps, Wrap } from '../styled'

const TemplatesTitle = styled.h3`
  margin: 8px 0 0;
  font: 700 20px/1.3 ${sans};
  letter-spacing: -0.015em;
  color: ${ink};
`

const Intro = styled.p`
  margin: -8px 0 0;
  line-height: 1.6;
`

// the prototype's .dd: the four deep dives, numbered, in two columns where there is room
const DeepDives = styled(Card)`
  ol {
    margin: 4px 0 0;
    padding-left: 22px;
    columns: 2;
    column-gap: 40px;
    font-size: 15px;
    color: ${ink};
  }

  li {
    margin-bottom: 5px;
    break-inside: avoid;
  }

  @media (max-width: 560px) {
    ol {
      columns: 1;
    }
  }
`

// the prototype's .finish: what the member leaves with, on a dark card
const Finish = styled.div`
  padding: 22px;
  border-radius: 22px;
  background: ${dark};
  color: ${onDark};

  ${Kicker} {
    color: ${cyan};
    margin-bottom: 10px;
  }

  li {
    color: #ffffff;
  }
`

// the prototype's .beyond: a dashed panel on the rest of the business
const Beyond = styled(Card)`
  border: 2px dashed #9fd3e2;

  p {
    color: ${ink};
  }
`

// each card reveals on its own
const StepCard = ({ step }) => (
  <Card {...useRevealProps()}>
    <Kicker>{step.when}</Kicker>
    <h3>{step.title}</h3>
    <p>{step.text}</p>
  </Card>
)

// the three templates of the monthly challenge, where a page has them
const Templates = ({ templates }) => (
  <Column $max={820} $gap={18}>
    <TemplatesTitle {...useRevealProps()}>{templates.title}</TemplatesTitle>
    <Intro {...useRevealProps()}>{templates.intro}</Intro>
    <Steps {...useRevealProps()}>
      {templates.steps.map((step, index) => (
        <Step key={step.title}>
          <StepNum aria-hidden="true">{index + 1}</StepNum>
          <div>
            <h3>{step.title}</h3>
            <p>
              {step.text}
              <b style={{ color: ink }}>{step.output}</b>
            </p>
          </div>
        </Step>
      ))}
    </Steps>
    <Small {...useRevealProps()}>{templates.note}</Small>
  </Column>
)

const RevealLede = ({ children }) => <Lede {...useRevealProps()}>{children}</Lede>

// the prototype's .fine under the whole section, where a page has one
const RevealNote = ({ children }) => <Small {...useRevealProps()}>{children}</Small>

const DeepDivesBlock = ({ deepDives }) => (
  <DeepDives {...useRevealProps()}>
    <Kicker>{deepDives.title}</Kicker>
    <ol>
      {deepDives.items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ol>
  </DeepDives>
)

const FinishBlock = ({ finish }) => (
  <Finish {...useRevealProps()}>
    <Kicker>{finish.title}</Kicker>
    <Bullets $ok style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))' }}>
      {finish.items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </Bullets>
  </Finish>
)

const BeyondBlock = ({ beyond }) => (
  <Beyond $tint {...useRevealProps()}>
    <Kicker>{beyond.kicker}</Kicker>
    <h3>{beyond.title}</h3>
    <p>{beyond.text}</p>
  </Beyond>
)

/**
 * "Your 30 days": the four steps from the day-one assessment to Martin's review, then whichever of these a page
 * has: the three templates, the deep dives, what the member finishes with, what lies beyond the focus, and a note.
 */
const JourneySection = () => {
  const { journey } = useFocus()

  return (
    <Section $ice id={IDS.how}>
      <Wrap>
        <Stack $gap="clamp(24px, 3vw, 32px)">
          <SectionHead>
            <Eyebrow {...useRevealProps()}>{journey.eyebrow}</Eyebrow>
            <H2 {...useRevealProps()}>
              {journey.headline} <Hi>{journey.headlineAccent}</Hi>
            </H2>
            {journey.lede && <RevealLede>{journey.lede}</RevealLede>}
          </SectionHead>
          <Grid $min={journey.columns === 2 ? 300 : 150}>
            {journey.steps.map((step) => (
              <StepCard key={step.when} step={step} />
            ))}
          </Grid>
          {journey.templates && <Templates templates={journey.templates} />}
          {journey.deepDives && <DeepDivesBlock deepDives={journey.deepDives} />}
          {journey.finish && <FinishBlock finish={journey.finish} />}
          {journey.beyond && <BeyondBlock beyond={journey.beyond} />}
          {journey.note && <RevealNote>{journey.note}</RevealNote>}
        </Stack>
      </Wrap>
    </Section>
  )
}

export default JourneySection
